import { useState, useRef, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Upload, FileText, Check, X, AlertCircle, ChevronRight, User } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImportBeneficiaireProps {
    onNavigateBack: () => void
}

type FieldKind = 'text' | 'date' | 'number' | 'bool'

interface ParsedField {
    dbKey: string
    label: string
    value: string
    kind: FieldKind
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BenefRow = Record<string, any>

// ─── Date helpers ─────────────────────────────────────────────────────────────

const MOIS_FR: Record<string, number> = {
    janvier: 0, février: 1, mars: 2, avril: 3, mai: 4, juin: 5,
    juillet: 6, août: 7, septembre: 8, octobre: 9, novembre: 10, décembre: 11,
}

function parseFrenchDate(raw: string): string | null {
    const trimmed = raw.trim()
    if (!trimmed) return null

    // dd/mm/yyyy
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (slashMatch) {
        const [, d, m, y] = slashMatch
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
    }

    // French textual date: may have leading day-of-week
    const parts = trimmed.toLowerCase().split(/\s+/)
    let day: string, moisStr: string, year: string
    if (parts.length >= 4) {
        // [jour_semaine, day, mois, year]
        day = parts[1]
        moisStr = parts[2]
        year = parts[3]
    } else if (parts.length === 3) {
        day = parts[0]
        moisStr = parts[1]
        year = parts[2]
    } else {
        return null
    }

    const monthIndex = MOIS_FR[moisStr]
    if (monthIndex === undefined) return null
    const dayNum = parseInt(day, 10)
    const yearNum = parseInt(year, 10)
    if (isNaN(dayNum) || isNaN(yearNum)) return null

    return `${yearNum}-${String(monthIndex + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
}

// ─── Multiline CSV parser ─────────────────────────────────────────────────────

function parseCSV(text: string): string[][] {
    const rows: string[][] = []
    const len = text.length
    let i = 0

    while (i < len) {
        const row: string[] = []
        // Parse fields for this row
        while (i < len) {
            if (text[i] === '"') {
                // Quoted field
                i++ // skip opening quote
                let field = ''
                while (i < len) {
                    if (text[i] === '"') {
                        if (i + 1 < len && text[i + 1] === '"') {
                            field += '"'
                            i += 2
                        } else {
                            i++ // skip closing quote
                            break
                        }
                    } else {
                        field += text[i]
                        i++
                    }
                }
                row.push(field)
            } else {
                // Unquoted field — read until ; or \n or \r\n
                let field = ''
                while (i < len && text[i] !== ';' && text[i] !== '\n' && text[i] !== '\r') {
                    field += text[i]
                    i++
                }
                row.push(field)
            }

            // After field: expect ; (next field) or line ending (end of row)
            if (i < len && text[i] === ';') {
                i++ // consume delimiter, continue row
            } else {
                break // end of row
            }
        }

        rows.push(row)

        // Consume line ending
        if (i < len && text[i] === '\r') i++
        if (i < len && text[i] === '\n') i++
    }

    return rows
}

// ─── Field mapping ────────────────────────────────────────────────────────────

// Sub-header rows to skip
const SKIP_LABELS = new Set(['numéro', 'qui?', 'description', 'ok?', 'commentaires'])

// Known section header labels (lowercase, trimmed) — used to avoid mistaking
// empty-value field rows for section changes
const KNOWN_SECTIONS = new Set([
    'généralités',
    "carte d'identité",
    'adresse de référence',
    'informations de contact',
    'accompagnement housing first',
    'revenu',
    'banque',
    'mutuelle',
    'informations médicales',
    'justice',
    'administration',
    'dettes',
    'lieu de vie',
    'logement',
    'historique logement',
    'charges en logement',
])

// section key → canonical label → [dbKey, kind]
type SectionMap = Record<string, [string, FieldKind]>

const SECTION_MAPS: Record<string, SectionMap> = {
    'généralités': {
        'nom': ['nom', 'text'],
        'prénom': ['prenom', 'text'],
        'nationalité': ['nationalite', 'text'],
        'sexe': ['sexe', 'text'],
        'genre': ['genre', 'text'],
        'date de naissance': ['date_naissance', 'date'],
        'age': ['age', 'number'],
        'lieu de naissance (ville, pays)': ['lieu_naissance', 'text'],
        'état civil': ['etat_civil', 'text'],
    },
    "carte d'identité": {
        'n° de registre national': ['registre_national', 'text'],
        "type de carte d'identité": ['type_carte_identite', 'text'],
        "n° de carte d'identité": ['numero_carte_identite', 'text'],
        'code pin': ['code_pin_carte_identite', 'text'],
        'date de fin de validité': ['date_fin_validite_carte_identite', 'date'],
        'personne en possession de la ci': ['personne_possession_ci', 'text'],
    },
    'adresse de référence': {
        'adresse de référence': ['adresse_reference', 'text'],
    },
    'informations de contact': {
        'n° de téléphone 1': ['telephone_1', 'text'],
        'n° de téléphone 2': ['telephone_2', 'text'],
        'n° de téléphone 3': ['telephone_3', 'text'],
        'adresse e-mail': ['email', 'text'],
    },
    'accompagnement housing first': {
        "statut de l'accompagnement": ['statut_accompagnement', 'text'],
        'référent.e': ['referente', 'text'],
        'date de première convention': ['date_premiere_convention', 'date'],
        'date de dernière convention': ['date_derniere_convention', 'date'],
        'situation résidentielle': ['situation_residentielle', 'text'],
        'bailleur': ['bailleur', 'text'],
        'nombre de logement depuis début du suivi': ['nombre_logements_depuis_debut_suivi', 'number'],
        'date de suspension ou termination': ['date_suspension_termination', 'date'],
        'cause de suspension ou de termination': ['cause_suspension_termination', 'text'],
    },
    'revenu': {
        'type de revenu': ['organisme_paiement', 'text'],   // intentional label swap
        'organisme de paiement': ['type_revenu', 'text'],   // intentional label swap
        'montant du revenu': ['montant_revenu', 'number'],
        'fréquence du revenu': ['frequence_revenu', 'text'],
        'as référent.e si cpas': ['as_referente_cpas', 'text'],
        'téléphone as référent.e': ['telephone_as_referente', 'text'],
    },
    'banque': {
        'nom de la banque': ['banque_nom', 'text'],
        'n° de compte': ['banque_numero_compte', 'text'],
        'n° de carte bancaire': ['banque_numero_carte', 'text'],
        'code pin': ['banque_code_pin', 'text'],
        'n° de dossier': ['banque_numero_dossier', 'text'],
    },
    'mutuelle': {
        'nom de la mutuelle': ['mutuelle_nom', 'text'],
        'assurabilité': ['assurabilite', 'text'],
        "n° d'assurabilité": ['assurabilite_numero', 'text'],
        'bim': ['bim', 'text'],
        'code de la vignette': ['bim_code_vignette', 'text'],
        'assurance complémentaire': ['assurance_complementaire', 'text'],
        'cotisations': ['assurance_complementaire_cotisations', 'text'],
        'spécificité': ['assurance_complementaire_specificite', 'text'],
    },
    'informations médicales': {
        'carte médicale': ['carte_medicale', 'text'],
        'maison médicale': ['maison_medicale', 'text'],
        'médecin référent': ['medecin_referent', 'text'],
        'pharmacie': ['pharmacie', 'text'],
    },
    'justice': {
        'problème en cours': ['justice_probleme_en_cours', 'text'],
        'type de problème': ['justice_type_probleme', 'text'],
        'as de justice': ['justice_as', 'text'],
        'nom': ['justice_nom', 'text'],
        'numéro de téléphone': ['justice_telephone', 'text'],
        'adresse e-mail': ['justice_email', 'text'],
        'avovat': ['justice_avocat', 'text'],
        'avocat': ['justice_avocat', 'text'],
        'téléphone de contact': ['justice_avocat_telephone', 'text'],
        'mail de contact': ['justice_avocat_mail', 'text'],
    },
    'administration': {
        "type d'administration": ['administration_type', 'text'],
        'nom': ['administration_nom', 'text'],
        'adresse': ['administration_adresse', 'text'],
        'numéro de téléphone': ['administration_telephone', 'text'],
        'adresse e-mail': ['administration_email', 'text'],
    },
    'dettes': {
        'dettes en cours': ['dettes_en_cours', 'text'],
        'type de dettes': ['dettes_type', 'text'],
        'médiateur de dettes': ['dettes_mediateur', 'text'],
        'nom': ['dettes_nom', 'text'],
        'numéro de téléphone': ['dettes_telephone', 'text'],
        'adresse e-mail': ['dettes_email', 'text'],
        'solution mise en place': ['dettes_solution', 'text'],
        'type de solution': ['dettes_type_solution', 'text'],
    },
    'lieu de vie': {
        'type de lieu de vie': ['lieu_vie_type', 'text'],
        'date de début': ['lieu_vie_date_debut', 'date'],
        'remarque': ['lieu_vie_remarque', 'text'],
        'rue': ['lieu_vie_rue', 'text'],
        'numéro de boîte': ['lieu_vie_numero_boite', 'text'],
        'commune': ['lieu_vie_commune', 'text'],
        'personne de contact': ['lieu_vie_personne_contact', 'text'],
        'téléphone de contact': ['lieu_vie_telephone_contact', 'text'],
    },
    'logement': {
        'type de logement': ['logement_type', 'text'],
        'rue': ['logement_rue', 'text'],
        'numéro de boîte': ['logement_numero_boite', 'text'],
        'etage': ['logement_etage', 'text'],
        "numéro d'appartement": ['logement_numero_appartement', 'text'],
        'commune': ['logement_commune', 'text'],
        'partenaire': ['logement_partenaire', 'text'],
        'type de bail/convention': ['logement_type_bail_convention', 'text'],
        'montant du loyer (réduction de tarif inclue, si applicable)': ['logement_montant_loyer', 'number'],
        'montant des charges communes': ['logement_montant_charges_communes', 'number'],
        'montant total du loyer (charges comprises)': ['logement_montant_total_loyer', 'number'],
        'date de signature de bail': ['logement_date_signature_bail', 'date'],
        'date de fin de bail': ['logement_date_fin_bail', 'date'],
        'type de gl': ['logement_type_gl', 'text'],
        'remboursement de gl': ['logement_remboursement_gl', 'text'],
    },
}

// Critères mapping: [bool field regex, bool dbKey, comment dbKey]
const CRITERES_MAP: Array<[RegExp, string, string]> = [
    [/^sans-abri$/i,                              'critere_sans_abri_ok',               'critere_sans_abri_commentaire'],
    [/^de longue dur/i,                           'critere_de_longue_duree_ok',          'critere_de_longue_duree_commentaire'],
    [/^fragile$/i,                                'critere_fragile_ok',                  'critere_fragile_commentaire'],
    [/nécessitant[\s\S]*accompagnement/i,         'critere_accompagnement_intensif_ok',  'critere_accompagnement_intensif_commentaire'],
    [/^avec source de revenus/i,                  'critere_source_revenus_ok',            'critere_source_revenus_commentaire'],
]

// ─── CSV → ParsedField[] ──────────────────────────────────────────────────────

function csvToFields(rows: string[][]): ParsedField[] {
    const fields: ParsedField[] = []
    let currentSection = ''
    let historiqueIndex = 0
    let mutuelleFinCount = 0
    let inCriteres = false

    // Track duplicates (same dbKey already set) – last write wins unless we de-dup
    const seenKeys = new Set<string>()

    const pushField = (dbKey: string, label: string, rawValue: string, kind: FieldKind) => {
        let value = rawValue.trim()
        if (!value) return

        if (kind === 'date') {
            const iso = parseFrenchDate(value)
            if (!iso) return
            value = iso
        }

        // Remove previous entry with same key (last value wins)
        const idx = fields.findIndex(f => f.dbKey === dbKey)
        if (idx !== -1) fields.splice(idx, 1)

        fields.push({ dbKey, label, value, kind })
        seenKeys.add(dbKey)
    }

    for (const row of rows) {
        const col0 = (row[0] ?? '').trim()
        const col1 = (row[1] ?? '').trim()
        const col1Lower = col1.toLowerCase().trim()
        const col2 = (row[2] ?? '').trim()
        const col3 = (row[3] ?? '').trim()
        const col4 = (row[4] ?? '').trim()

        // Skip entirely empty rows
        if (!col1 && !col2) continue

        // Skip known sub-header rows
        if (SKIP_LABELS.has(col1Lower)) continue

        // Detect section headers by name — never rely on col2 being empty, because
        // empty-value field rows also have col2="" and would otherwise reset the section.
        if (col0 !== 'VRAI' && col1) {
            if (col1Lower.includes('tableau récapitulatif')) {
                inCriteres = true
                currentSection = 'critères'
                continue
            }
            if (KNOWN_SECTIONS.has(col1Lower)) {
                inCriteres = false
                historiqueIndex = 0
                mutuelleFinCount = 0
                currentSection = col1Lower
                continue
            }
        }

        // ── Critères section ──
        if (inCriteres && col0 === 'VRAI') {
            // col1 = critère name (may contain newlines), col2 = description, col3 = "OK"/"Pas OK", col4 = commentaire
            const critereName = col1  // keep original with possible newlines for regex matching
            const okVal = col3.toLowerCase()
            if (critereName) {
                for (const [pattern, boolKey, commentKey] of CRITERES_MAP) {
                    if (pattern.test(critereName)) {
                        if (col3) {
                            const boolVal = okVal === 'ok' ? 'true' : 'false'
                            pushField(boolKey, col1.replace(/\s+/g, ' ').trim(), boolVal, 'bool')
                        }
                        if (col4) {
                            pushField(commentKey, col1.replace(/\s+/g, ' ').trim() + ' (commentaire)', col4, 'text')
                        }
                        break
                    }
                }
            }
            continue
        }
        if (inCriteres) continue

        // ── Historique Logement section ──
        if (currentSection === 'historique logement') {
            if (col1Lower.startsWith('adresse')) {
                // "Adresse 1", "Adresse 2", etc.
                const numMatch = col1.match(/\d+/)
                if (numMatch) {
                    historiqueIndex = parseInt(numMatch[0], 10)
                }
                if (col2 && historiqueIndex > 0) {
                    pushField(
                        `historique_logement_${historiqueIndex}_adresse`,
                        `Adresse ${historiqueIndex} (historique)`,
                        col2,
                        'text',
                    )
                }
                continue
            }
            if (historiqueIndex > 0) {
                if (col1Lower === 'date de signature du bail') {
                    pushField(
                        `historique_logement_${historiqueIndex}_date_signature_bail`,
                        `Historique ${historiqueIndex} - Date signature bail`,
                        col2,
                        'date',
                    )
                    continue
                }
                if (col1Lower === 'date du renom') {
                    pushField(
                        `historique_logement_${historiqueIndex}_date_renom`,
                        `Historique ${historiqueIndex} - Date du renom`,
                        col2,
                        'date',
                    )
                    continue
                }
                if (col1Lower === 'raison invoquée') {
                    pushField(
                        `historique_logement_${historiqueIndex}_raison_invoquee`,
                        `Historique ${historiqueIndex} - Raison invoquée`,
                        col2,
                        'text',
                    )
                    continue
                }
            }
            continue
        }

        // ── Mutuelle: "Date de fin" appears twice ──
        if (currentSection === 'mutuelle' && col1Lower === 'date de fin') {
            mutuelleFinCount++
            if (mutuelleFinCount === 1) {
                pushField('assurabilite_date_fin', 'Assurabilité - Date de fin', col2, 'date')
            } else {
                pushField('bim_date_fin', 'BIM - Date de fin', col2, 'date')
            }
            continue
        }

        // ── Informations de contact: phone rows with col3 "Qui?" ──
        if (currentSection === 'informations de contact') {
            const sectionMap = SECTION_MAPS['informations de contact'] ?? {}
            const mapping = sectionMap[col1Lower]
            if (mapping) {
                const [dbKey, kind] = mapping
                pushField(dbKey, col1, col2, kind as FieldKind)
                // col3 = "Qui?"
                if (col3 && dbKey.startsWith('telephone_')) {
                    const quiKey = `${dbKey}_qui`
                    pushField(quiKey, `${col1} - Qui?`, col3, 'text')
                }
                continue
            }
            continue
        }

        // ── Generic section lookup ──
        const sectionMap = SECTION_MAPS[currentSection]
        if (sectionMap) {
            const mapping = sectionMap[col1Lower]
            if (mapping) {
                const [dbKey, kind] = mapping
                pushField(dbKey, col1, col2, kind as FieldKind)
            }
        }
    }

    return fields
}

// ─── DB label lookup (for display in conflict table) ──────────────────────────

const DB_LABELS: Record<string, string> = {
    nom: 'Nom',
    prenom: 'Prénom',
    nationalite: 'Nationalité',
    sexe: 'Sexe',
    genre: 'Genre',
    date_naissance: 'Date de naissance',
    age: 'Âge',
    lieu_naissance: 'Lieu de naissance',
    etat_civil: 'État civil',
    registre_national: 'N° registre national',
    type_carte_identite: "Type carte d'identité",
    numero_carte_identite: "N° carte d'identité",
    code_pin_carte_identite: 'Code PIN CI',
    date_fin_validite_carte_identite: 'Date fin validité CI',
    personne_possession_ci: 'Personne possession CI',
    adresse_reference: 'Adresse de référence',
    telephone_1: 'Téléphone 1',
    telephone_1_qui: 'Tél 1 - Qui?',
    telephone_2: 'Téléphone 2',
    telephone_2_qui: 'Tél 2 - Qui?',
    telephone_3: 'Téléphone 3',
    telephone_3_qui: 'Tél 3 - Qui?',
    email: 'Email',
    statut_accompagnement: 'Statut accompagnement',
    referente: 'Référent.e',
    date_premiere_convention: 'Date 1ère convention',
    date_derniere_convention: 'Date dernière convention',
    situation_residentielle: 'Situation résidentielle',
    bailleur: 'Bailleur',
    nombre_logements_depuis_debut_suivi: 'Nb logements depuis début',
    date_suspension_termination: 'Date suspension/termination',
    cause_suspension_termination: 'Cause suspension/termination',
    organisme_paiement: 'Organisme paiement',
    type_revenu: 'Type de revenu',
    montant_revenu: 'Montant revenu',
    frequence_revenu: 'Fréquence revenu',
    as_referente_cpas: 'AS référent.e CPAS',
    telephone_as_referente: 'Tél AS référent.e',
    banque_nom: 'Banque - Nom',
    banque_numero_compte: 'Banque - N° compte',
    banque_numero_carte: 'Banque - N° carte',
    banque_code_pin: 'Banque - Code PIN',
    banque_numero_dossier: 'Banque - N° dossier',
    mutuelle_nom: 'Mutuelle - Nom',
    assurabilite: 'Assurabilité',
    assurabilite_date_fin: 'Assurabilité - Date fin',
    assurabilite_numero: "N° d'assurabilité",
    bim: 'BIM',
    bim_date_fin: 'BIM - Date fin',
    bim_code_vignette: 'BIM - Code vignette',
    assurance_complementaire: 'Assurance complémentaire',
    assurance_complementaire_cotisations: 'Assurance compl. - Cotisations',
    assurance_complementaire_specificite: 'Assurance compl. - Spécificité',
    carte_medicale: 'Carte médicale',
    maison_medicale: 'Maison médicale',
    medecin_referent: 'Médecin référent',
    pharmacie: 'Pharmacie',
    justice_probleme_en_cours: 'Justice - Problème en cours',
    justice_type_probleme: 'Justice - Type problème',
    justice_as: 'Justice - AS',
    justice_nom: 'Justice - Nom',
    justice_telephone: 'Justice - Téléphone',
    justice_email: 'Justice - Email',
    justice_avocat: 'Justice - Avocat',
    justice_avocat_telephone: 'Justice - Tél avocat',
    justice_avocat_mail: 'Justice - Mail avocat',
    administration_type: 'Administration - Type',
    administration_nom: 'Administration - Nom',
    administration_adresse: 'Administration - Adresse',
    administration_telephone: 'Administration - Téléphone',
    administration_email: 'Administration - Email',
    dettes_en_cours: 'Dettes en cours',
    dettes_type: 'Dettes - Type',
    dettes_mediateur: 'Dettes - Médiateur',
    dettes_nom: 'Dettes - Nom',
    dettes_telephone: 'Dettes - Téléphone',
    dettes_email: 'Dettes - Email',
    dettes_solution: 'Dettes - Solution',
    dettes_type_solution: 'Dettes - Type solution',
    lieu_vie_type: 'Lieu de vie - Type',
    lieu_vie_date_debut: 'Lieu de vie - Date début',
    lieu_vie_remarque: 'Lieu de vie - Remarque',
    lieu_vie_rue: 'Lieu de vie - Rue',
    lieu_vie_numero_boite: 'Lieu de vie - N° boîte',
    lieu_vie_commune: 'Lieu de vie - Commune',
    lieu_vie_personne_contact: 'Lieu de vie - Personne contact',
    lieu_vie_telephone_contact: 'Lieu de vie - Tél contact',
    logement_type: 'Logement - Type',
    logement_rue: 'Logement - Rue',
    logement_numero_boite: 'Logement - N° boîte',
    logement_etage: 'Logement - Étage',
    logement_numero_appartement: 'Logement - N° appartement',
    logement_commune: 'Logement - Commune',
    logement_partenaire: 'Logement - Partenaire',
    logement_type_bail_convention: 'Logement - Type bail/convention',
    logement_montant_loyer: 'Logement - Montant loyer',
    logement_montant_charges_communes: 'Logement - Charges communes',
    logement_montant_total_loyer: 'Logement - Montant total loyer',
    logement_date_signature_bail: 'Logement - Date signature bail',
    logement_date_fin_bail: 'Logement - Date fin bail',
    logement_type_gl: 'Logement - Type GL',
    logement_remboursement_gl: 'Logement - Remboursement GL',
    historique_logement_1_adresse: 'Historique 1 - Adresse',
    historique_logement_1_date_signature_bail: 'Historique 1 - Date signature bail',
    historique_logement_1_date_renom: 'Historique 1 - Date renom',
    historique_logement_1_raison_invoquee: 'Historique 1 - Raison invoquée',
    historique_logement_2_adresse: 'Historique 2 - Adresse',
    historique_logement_2_date_signature_bail: 'Historique 2 - Date signature bail',
    historique_logement_2_date_renom: 'Historique 2 - Date renom',
    historique_logement_2_raison_invoquee: 'Historique 2 - Raison invoquée',
    historique_logement_3_adresse: 'Historique 3 - Adresse',
    historique_logement_3_date_signature_bail: 'Historique 3 - Date signature bail',
    historique_logement_3_date_renom: 'Historique 3 - Date renom',
    historique_logement_3_raison_invoquee: 'Historique 3 - Raison invoquée',
    critere_sans_abri_ok: 'Critère sans-abri',
    critere_sans_abri_commentaire: 'Critère sans-abri — commentaire',
    critere_de_longue_duree_ok: 'Critère longue durée',
    critere_de_longue_duree_commentaire: 'Critère longue durée — commentaire',
    critere_fragile_ok: 'Critère fragile',
    critere_fragile_commentaire: 'Critère fragile — commentaire',
    critere_accompagnement_intensif_ok: 'Critère accompagnement intensif',
    critere_accompagnement_intensif_commentaire: 'Critère accompagnement intensif — commentaire',
    critere_source_revenus_ok: 'Critère source revenus',
    critere_source_revenus_commentaire: 'Critère source revenus — commentaire',
}

function dbLabel(key: string): string {
    return DB_LABELS[key] ?? key
}

// ─── Value coercion for DB insert/update ─────────────────────────────────────

function coerceValue(field: ParsedField): string | number | boolean | null {
    const v = field.value
    if (field.kind === 'bool') {
        return v === 'true'
    }
    if (field.kind === 'number') {
        const n = parseFloat(v.replace(',', '.'))
        if (isNaN(n)) return null
        return n
    }
    return v
}

// ─── Component ────────────────────────────────────────────────────────────────

type Step = 'upload' | 'review' | 'conflicts' | 'done'

interface ConflictItem {
    field: ParsedField
    existingValue: string
    overwrite: boolean
}

export const ImportBeneficiaire = ({ onNavigateBack }: ImportBeneficiaireProps) => {
    const [step, setStep] = useState<Step>('upload')
    const [dragging, setDragging] = useState(false)
    const [fileName, setFileName] = useState('')
    const [parsedFields, setParsedFields] = useState<ParsedField[]>([])
    const [allBeneficiaires, setAllBeneficiaires] = useState<BenefRow[]>([])
    const [selectedBenefId, setSelectedBenefId] = useState<string>('__new__')
    const [autoMatchName, setAutoMatchName] = useState<string | null>(null)
    const [showAllFields, setShowAllFields] = useState(false)
    const [conflicts, setConflicts] = useState<ConflictItem[]>([])
    const [loadingStep, setLoadingStep] = useState(false)
    const [doneCount, setDoneCount] = useState(0)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // ── Step 1: handle file ──

    const processFile = useCallback((file: File) => {
        setErrorMsg(null)
        setFileName(file.name)
        const reader = new FileReader()
        reader.onload = async (e) => {
            try {
                const text = e.target?.result as string
                const rows = parseCSV(text)
                const fields = csvToFields(rows)
                setParsedFields(fields)

                // Load all beneficiaires for the dropdown + auto-match
                const { data } = await supabase
                    .from('beneficiaries')
                    .select('id, name, prenom, nom, is_active')
                    .order('name')
                if (data) {
                    setAllBeneficiaires(data as BenefRow[])
                    // Auto-match: find first name from CSV
                    const csvPrenom = fields.find(f => f.dbKey === 'prenom')?.value?.toUpperCase()
                    if (csvPrenom) {
                        const match = (data as BenefRow[]).find(b => {
                            const namePart = (b.name ?? '').split(' ')[0].toUpperCase()
                            return namePart === csvPrenom
                        })
                        if (match) {
                            setSelectedBenefId(match.id)
                            setAutoMatchName(match.name)
                        } else {
                            setSelectedBenefId('__new__')
                            setAutoMatchName(null)
                        }
                    }
                }
                setStep('review')
            } catch (err) {
                setErrorMsg(`Erreur lors du parsing : ${err instanceof Error ? err.message : String(err)}`)
            }
        }
        reader.readAsText(file, 'windows-1252')
    }, [])

    const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files[0]
        if (file) processFile(file)
    }, [processFile])

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) processFile(file)
    }, [processFile])

    // ── Step 2 → 3: continue ──

    const handleContinue = useCallback(async () => {
        if (selectedBenefId === '__new__') {
            // No conflicts for new beneficiary
            setConflicts([])
            setStep('conflicts')
            return
        }
        setLoadingStep(true)
        try {
            const { data: existing } = await supabase
                .from('beneficiaries')
                .select('*')
                .eq('id', selectedBenefId)
                .single()

            if (!existing) {
                setConflicts([])
                setStep('conflicts')
                setLoadingStep(false)
                return
            }

            const conflictItems: ConflictItem[] = []
            for (const field of parsedFields) {
                const existingRaw = existing[field.dbKey]
                if (existingRaw == null || existingRaw === '') continue
                const existingStr = String(existingRaw)
                if (existingStr === field.value) continue
                conflictItems.push({
                    field,
                    existingValue: existingStr,
                    overwrite: false,
                })
            }

            if (conflictItems.length === 0) {
                // No conflicts — go straight to import
                await doImport(parsedFields, selectedBenefId, false)
            } else {
                setConflicts(conflictItems)
                setStep('conflicts')
            }
        } catch (err) {
            setErrorMsg(`Erreur : ${err instanceof Error ? err.message : String(err)}`)
        }
        setLoadingStep(false)
    }, [selectedBenefId, parsedFields])  // eslint-disable-line react-hooks/exhaustive-deps

    // ── Step 3: import ──

    const doImport = useCallback(async (
        fields: ParsedField[],
        benefId: string,
        isConflictStep: boolean,
        conflictList?: ConflictItem[],
    ) => {
        setLoadingStep(true)
        setErrorMsg(null)
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const updateData: Record<string, any> = {}

            const conflictOverwriteKeys = new Set(
                (conflictList ?? []).filter(c => c.overwrite).map(c => c.field.dbKey),
            )
            const conflictSkipKeys = new Set(
                (conflictList ?? []).filter(c => !c.overwrite).map(c => c.field.dbKey),
            )

            for (const field of fields) {
                if (isConflictStep && conflictSkipKeys.has(field.dbKey)) continue
                const v = coerceValue(field)
                if (v === null) continue
                updateData[field.dbKey] = v
            }

            let count = Object.keys(updateData).length

            if (benefId === '__new__') {
                updateData.is_active = true
                const prenom = updateData.prenom ?? ''
                const nom = updateData.nom ?? ''
                if (prenom || nom) {
                    updateData.name = `${prenom} ${nom}`.trim()
                }
                const { error } = await supabase.from('beneficiaries').insert(updateData)
                if (error) throw error
            } else {
                const { error } = await supabase
                    .from('beneficiaries')
                    .update(updateData)
                    .eq('id', benefId)
                if (error) throw error
            }

            // Account for overwritten conflicts
            if (isConflictStep) {
                count = Object.keys(updateData).length
            }

            setDoneCount(count)
            setStep('done')
        } catch (err) {
            setErrorMsg(`Erreur lors de l'import : ${err instanceof Error ? err.message : String(err)}`)
        }
        setLoadingStep(false)
    }, [])

    const handleImport = useCallback(() => {
        doImport(parsedFields, selectedBenefId, true, conflicts)
    }, [parsedFields, selectedBenefId, conflicts, doImport])

    // ── Render helpers ──

    const displayedFields = showAllFields ? parsedFields : parsedFields.slice(0, 50)

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="bg-gray-50">
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

                {/* Error banner */}
                {errorMsg && (
                    <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{errorMsg}</span>
                        <button
                            type="button"
                            className="ml-auto shrink-0 text-red-400 hover:text-red-600"
                            onClick={() => setErrorMsg(null)}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {/* ── STEP 1: Upload ── */}
                {step === 'upload' && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h2 className="font-semibold text-gray-800">Étape 1 — Sélectionner le fichier CSV</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Fichier encodé en Windows-1252, délimiteur point-virgule</p>
                        </div>
                        <div className="p-6">
                            <div
                                className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
                                    ${dragging
                                        ? 'border-blue-400 bg-blue-50'
                                        : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'
                                    }`}
                                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={handleFileDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleFileInput}
                                />
                                <Upload className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-600 font-medium">
                                    {dragging
                                        ? 'Déposez le fichier ici'
                                        : 'Glissez-déposez ou cliquez pour parcourir'
                                    }
                                </p>
                                <p className="text-sm text-gray-400 mt-1">Fichiers .csv uniquement</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 2: Review ── */}
                {step === 'review' && (
                    <div className="space-y-4">
                        {/* File info */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="font-semibold text-gray-800">Étape 2 — Vérifier les données</h2>
                            </div>
                            <div className="px-6 py-4 flex items-center gap-3 text-sm text-gray-700">
                                <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                                <span className="font-medium truncate">{fileName}</span>
                                <span className="ml-auto shrink-0 text-gray-500">
                                    {parsedFields.length} champ{parsedFields.length > 1 ? 's' : ''} trouvé{parsedFields.length > 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>

                        {/* Target beneficiary */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                                <User className="h-4 w-4 text-gray-500" />
                                <h3 className="font-semibold text-gray-800 text-sm">Bénéficiaire cible</h3>
                            </div>
                            <div className="px-6 py-4 space-y-3">
                                {autoMatchName && selectedBenefId !== '__new__' && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full font-medium text-xs">
                                            <Check className="h-3 w-3" />
                                            Correspondance trouvée
                                        </span>
                                        <span className="text-gray-700 font-medium">{autoMatchName}</span>
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                        Bénéficiaire
                                    </label>
                                    <select
                                        value={selectedBenefId}
                                        onChange={e => setSelectedBenefId(e.target.value)}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    >
                                        <option value="__new__">— Nouveau bénéficiaire —</option>
                                        {allBeneficiaires.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {[b.prenom, b.nom].filter(Boolean).join(' ') || b.name || b.id}
                                                {b.is_active === false ? ' (inactif)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Parsed fields table */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-800 text-sm">Champs parsés</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-1/3">Label CSV</th>
                                            <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-1/4">Champ DB</th>
                                            <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Valeur</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {displayedFields.map((f, i) => (
                                            <tr key={i} className="hover:bg-gray-50/50">
                                                <td className="px-4 py-2 text-gray-600">{f.label}</td>
                                                <td className="px-4 py-2 font-mono text-gray-500">{f.dbKey}</td>
                                                <td className="px-4 py-2 text-gray-800 truncate max-w-xs">
                                                    {f.kind === 'bool'
                                                        ? (f.value === 'true'
                                                            ? <span className="text-green-600 font-medium">OK</span>
                                                            : <span className="text-red-500 font-medium">Pas OK</span>)
                                                        : f.value
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {parsedFields.length > 50 && !showAllFields && (
                                <div className="px-4 py-3 border-t border-gray-100 text-center">
                                    <button
                                        type="button"
                                        className="text-xs text-blue-600 hover:underline"
                                        onClick={() => setShowAllFields(true)}
                                    >
                                        Afficher les {parsedFields.length - 50} champs restants
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <Button
                                onClick={handleContinue}
                                disabled={loadingStep}
                                className="flex items-center gap-2"
                            >
                                {loadingStep
                                    ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                    : <ChevronRight className="h-4 w-4" />
                                }
                                Continuer
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── STEP 3: Conflicts ── */}
                {step === 'conflicts' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="font-semibold text-gray-800">
                                    Étape 3 — {conflicts.length > 0 ? 'Résoudre les conflits' : 'Confirmer l\'import'}
                                </h2>
                                {conflicts.length > 0 && (
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {conflicts.length} champ{conflicts.length > 1 ? 's' : ''} en conflit avec la base de données
                                    </p>
                                )}
                            </div>

                            {conflicts.length > 0 ? (
                                <>
                                    {/* Bulk buttons */}
                                    <div className="px-6 py-3 border-b border-gray-100 flex gap-3">
                                        <button
                                            type="button"
                                            className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                                            onClick={() => setConflicts(prev => prev.map(c => ({ ...c, overwrite: true })))}
                                        >
                                            Tout écraser
                                        </button>
                                        <button
                                            type="button"
                                            className="text-xs px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                                            onClick={() => setConflicts(prev => prev.map(c => ({ ...c, overwrite: false })))}
                                        >
                                            Tout garder
                                        </button>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gray-50 border-b border-gray-100">
                                                <tr>
                                                    <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-1/5">Champ</th>
                                                    <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-2/5">Valeur actuelle (DB)</th>
                                                    <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-2/5">Valeur CSV</th>
                                                    <th className="text-center px-4 py-2.5 text-gray-500 font-medium">Écraser?</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {conflicts.map((c, i) => (
                                                    <tr key={i} className={`hover:bg-gray-50/50 ${c.overwrite ? 'bg-orange-50/30' : ''}`}>
                                                        <td className="px-4 py-2 text-gray-700 font-medium">
                                                            {dbLabel(c.field.dbKey)}
                                                        </td>
                                                        <td className="px-4 py-2 text-red-600 truncate max-w-xs">
                                                            {c.existingValue}
                                                        </td>
                                                        <td className="px-4 py-2 text-green-700 truncate max-w-xs">
                                                            {c.field.value}
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={c.overwrite}
                                                                onChange={e => setConflicts(prev =>
                                                                    prev.map((item, idx) =>
                                                                        idx === i ? { ...item, overwrite: e.target.checked } : item
                                                                    )
                                                                )}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="px-6 py-6 text-sm text-gray-600">
                                    {selectedBenefId === '__new__'
                                        ? 'Un nouveau bénéficiaire sera créé avec les champs importés.'
                                        : 'Aucun conflit détecté. Tous les champs CSV seront importés.'
                                    }
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between">
                            <Button
                                variant="ghost"
                                onClick={() => setStep('review')}
                                disabled={loadingStep}
                            >
                                Retour
                            </Button>
                            <Button
                                onClick={handleImport}
                                disabled={loadingStep}
                                className="flex items-center gap-2"
                            >
                                {loadingStep
                                    ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                    : <Check className="h-4 w-4" />
                                }
                                Importer
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── STEP done ── */}
                {step === 'done' && (
                    <div className="bg-white rounded-xl border border-green-200 shadow-sm">
                        <div className="px-6 py-10 text-center space-y-4">
                            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mx-auto">
                                <Check className="h-7 w-7 text-green-600" />
                            </div>
                            <div>
                                <p className="text-lg font-semibold text-gray-900">Import réussi</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {doneCount} champ{doneCount > 1 ? 's' : ''} importé{doneCount > 1 ? 's' : ''} avec succès
                                </p>
                            </div>
                            <div className="flex justify-center gap-3 pt-2">
                                <Button variant="ghost" onClick={onNavigateBack}>
                                    Retour à la liste
                                </Button>
                                <Button
                                    onClick={() => {
                                        setStep('upload')
                                        setFileName('')
                                        setParsedFields([])
                                        setConflicts([])
                                        setSelectedBenefId('__new__')
                                        setAutoMatchName(null)
                                        setShowAllFields(false)
                                        setErrorMsg(null)
                                    }}
                                >
                                    Importer un autre fichier
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}
